import {lerp} from "ol/math";
import {GritUserService} from "../../services/grit/grit.user.service";
import {Coordinate} from "ol/coordinate";
import {fromLonLat} from "ol/proj";
import {RCS} from "../../models/grit/grit.coordinates";
import Point from "ol/geom/Point";
import {getDistance, getLength} from "ol/sphere";
import LineString from "ol/geom/LineString";
import {ConfigurationModel} from "../../models/configuration";

export class GritUtils {
  static fsplDistance(frequency: number, Pt: number, Gt: number, Pl: number, Pr: number, Gr: number): number {
    // Pt = device.power
    // Gt = device.verticalGain
    // Pl = device.polarisationLoss
    // Pr = targetReceiver.threshold.dBW
    // Gr = targetReceiver.gain
    
    let fspl = (Pt + 30) + Gt - (Pr + 30) + Gr;  // +30 converts from dBW to dbm
    let d = Math.pow(10, (fspl + Pl - 32.44 - 20 * Math.log10(frequency / 1000000)) / 20) / 1.852 // (/ 1000000) converts Hz to Mhz, (/ 1.852) converts Km to NM
    
    return  d == undefined || d == Infinity ? 0 : Math.round(d);
  }

  static fsplPower(frequency: number, d: number, Gt: number, Pr: number, Gr: number) : number{
    let fspl = 20 * Math.log10(d) + 20 * Math.log10(frequency) - 84.2;
    return Number((fspl + Pr - Gr - Gt).toFixed(4));
  }

  //Height in meters. Returns radius in meters
  static radioHorizonRadius(h1 : number, h2: number) : number {
    return 4.12 * (Math.sqrt(h1) + Math.sqrt(h2)) * 1000;
  }

  static freeSpaceRadius(range : number, fl : number, h :number) : number {
    let diff = Math.pow(range, 2) - Math.pow(fl - h, 2);
    return diff < 0 ? 0 : Math.sqrt(diff);
  }

  static flRadius(fl: number, range: number, h : number){
    let a = this.radioHorizonRadius(h, fl) ?? 0;
    let b = this.freeSpaceRadius(range, fl, h) ?? 0;
    return Math.min(a, b);
  }

  static roundNearestNumber(value: number, nearestNumber: number, up: boolean = true) {
    value = Math.round(value);
    let remainder = value % nearestNumber;

    if (remainder != 0){
      if (up) value = value + nearestNumber - remainder
      else if (!up) value = value - nearestNumber + remainder
    }

    return value;
  }

  static mergeCyclicAngles(intervals : number[][]) {

    // Add 360 to end points for handling the cyclic condition
    let intervals2 = []
    intervals.forEach(([start, end]) => {
      if(end < start){
        return intervals2.push(...[[start, 360], [0, end]]);
      }
      else{
        return intervals2.push([start, end])
      }
    });

    intervals2.sort((a,b) => a[0] - b[0]);

    let merged = {
      interval : intervals2.shift(),
      tbd : []
    };

    for(let inter of intervals2){
      if(inter[0] <= merged.interval[1] && inter[1] > merged.interval[1]){
        merged.interval[1] = inter[1]
      }
      else{
        merged.tbd.push(inter)
      }
    }

    //Check if other intervals can be added at the start
    if(merged.tbd.length > 0){
      merged.tbd.sort((a,b) => b[1] - a[1]);

      for(let inter of merged.tbd){
        if(inter[1] == 360 && merged.interval[0] == 0){
          merged.interval[0] = inter[0];
        }else if(inter[1] >= merged.interval[0] && inter[0] < merged.interval[0]){
          merged.interval[0] = inter[0];
        }
        else{
          return null; // Not able to merge all intervals
        }
      }
    }

    return merged.interval;
  }

  /*
  *@Param All in meters
  *@Return Flight Level
  * */
  static FLRadioHorizon(radius: number, h: number) {
    let FLfeet = ConfigurationModel.metersToFeet(Math.pow(radius/4120 - Math.sqrt(h) ,2));
    return GritUtils.roundNearestNumber(FLfeet, 500) / 100;
  }

  /*
  *@Param All in meters
  *@Return Flight Level
  */
  static FLFreeSpaceRadius (radius: number, range: number, h: number){
    if (radius > range) return -1

    let FLfeet = ConfigurationModel.metersToFeet(h + Math.sqrt(Math.pow(range, 2) - Math.pow(radius,2)));
    return GritUtils.roundNearestNumber(FLfeet, 500) / 100;
  }

  /*
  * @Return Distance in meters
  * */
  static distanceBetween(coordLonLat1: Coordinate, coordLonLat2: Coordinate){
      let c1 = fromLonLat(coordLonLat1, RCS.EPSG_3857);
      let c2 = fromLonLat(coordLonLat2, RCS.EPSG_3857);

      return getLength(new LineString([c1, c2], "XY"), {projection: RCS.EPSG_3857});
  }
}
